import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import SingleDatePicker from '../../../../../components/ui/SingleDatePicker';
import { MAX_SERIES_OCCURRENCES, RecurrencePattern } from '../../../../../utils/recurrence';

/**
 * How a series ends: after N dates, or on a date. Shared by the create
 * wizard's pattern form and the extend panel's "continue the series' rule"
 * mode, which asks only this.
 */
interface SeriesEndConditionFieldsProps {
    pattern: Pick<RecurrencePattern, 'endCondition' | 'endDate' | 'occurrences'>;
    onChange: (updates: Partial<RecurrencePattern>) => void;
}

const SeriesEndConditionFields: React.FC<SeriesEndConditionFieldsProps> = ({ pattern, onChange }) => {
    const { t } = useTranslation();
    const updatePattern = onChange;

    return (
        <>
            <label className={'mb-2 block text-sm font-medium text-gray-700'}>
                {t('series_booking.recurrence.series_end')}
            </label>
            <div className={'flex flex-col gap-3'}>
                <label className={'flex items-center gap-2'}>
                    <input
                        type={'radio'}
                        checked={pattern.endCondition === 'afterOccurrences'}
                        onChange={() => updatePattern({ endCondition: 'afterOccurrences' })}
                        className={'text-primary'}
                    />
                    <span className={'text-sm text-gray-700'}>
                        {t('series_booking.recurrence.after')}
                    </span>
                    <input
                        type={'number'}
                        min={1}
                        max={MAX_SERIES_OCCURRENCES}
                        value={pattern.occurrences}
                        onChange={(e) => updatePattern({
                            occurrences: Math.min(MAX_SERIES_OCCURRENCES, Math.max(1, parseInt(e.target.value) || 1)),
                        })}
                        disabled={pattern.endCondition !== 'afterOccurrences'}
                        className={'w-20 rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400'}
                    />
                    <span className={'text-sm text-gray-700'}>
                        {t('series_booking.recurrence.after_unit', { count: pattern.occurrences })}
                    </span>
                </label>
                <label className={'flex items-center gap-2'}>
                    <input
                        type={'radio'}
                        checked={pattern.endCondition === 'untilDate'}
                        onChange={() => updatePattern({ endCondition: 'untilDate' })}
                        className={'text-primary'}
                    />
                    <span className={'text-sm text-gray-700'}>
                        {t('series_booking.recurrence.until')}
                    </span>
                    <div className={'w-44'}>
                        <SingleDatePicker
                            value={pattern.endDate ?? null}
                            onChange={(date) => updatePattern({ endDate: date ?? null })}
                            disabled={pattern.endCondition !== 'untilDate'}
                            disablePast
                            minDate={dayjs().add(1, 'day')}
                            placeholder={t('series_booking.recurrence.pick_date')}
                        />
                    </div>
                </label>
            </div>
        </>
    );
};

export default SeriesEndConditionFields;
